/**
 * SCR-020 — Danh sách học viên & lịch lớp (Teacher)
 * Route: /teacher/classes/:classId/roster
 * Read-only. Mobile: card list 1 col → table md+. Schedule as card list.
 */
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, RefreshCw, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTeacherRoster } from '@client/lib/queries/use-teacher';
import type { RosterEnrollment, TeacherClassSchedule } from '@client/lib/queries/use-teacher';

export const Route = createFileRoute('/teacher/classes/$classId/roster')({
  component: RosterTab,
});

const WEEKDAY_LABELS: Record<string, string> = {
  mon: 'Thứ Hai', tue: 'Thứ Ba', wed: 'Thứ Tư',
  thu: 'Thứ Năm', fri: 'Thứ Sáu', sat: 'Thứ Bảy', sun: 'Chủ Nhật',
};

function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function removeAccents(str: string): string {
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// ---------------------------------------------------------------------------
function RosterTab() {
  const { classId } = Route.useParams();
  const { data, isLoading, isError, refetch } = useTeacherRoster(classId);
  const [search, setSearch] = useState('');

  const enrollments = data?.enrollments ?? [];
  const schedules = data?.schedules ?? [];

  const filtered = search
    ? enrollments.filter((e) =>
        removeAccents(e.student.fullName).includes(removeAccents(search)) ||
        e.student.studentCode.toLowerCase().includes(search.toLowerCase()),
      )
    : enrollments;

  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được danh sách học viên.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          {/* Sĩ số + search */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                Sĩ số:{' '}
                <span className={data.currentEnrollment >= data.capacity ? 'text-destructive font-semibold' : ''}>
                  {data.currentEnrollment}/{data.capacity}
                </span>
              </span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm học viên..."
                className="pl-8 h-9 w-full"
                maxLength={100}
              />
            </div>
          </div>

          {/* Student list — cards on mobile, table md+ */}
          {enrollments.length === 0 ? (
            <EmptySection message="Lớp chưa có học viên ghi danh." />
          ) : filtered.length === 0 ? (
            <EmptySection message="Không tìm thấy học viên." />
          ) : (
            <>
              {/* Mobile cards — hidden on md+ */}
              <div className="flex flex-col gap-3 md:hidden">
                {filtered.map((e) => (
                  <StudentCard key={e.id} enrollment={e} />
                ))}
              </div>

              {/* Table — hidden on mobile */}
              <div className="hidden md:block rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                      <th className="px-4 py-3 text-left font-medium">Mã HV</th>
                      <th className="px-4 py-3 text-left font-medium">Họ tên</th>
                      <th className="px-4 py-3 text-left font-medium">Tuổi</th>
                      <th className="px-4 py-3 text-left font-medium">Giới tính</th>
                      <th className="px-4 py-3 text-left font-medium">Ghi danh</th>
                      <th className="px-4 py-3 text-left font-medium">Trạng thái</th>
                      <th className="px-4 py-3 text-left font-medium">Liên hệ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e) => (
                      <StudentRow key={e.id} enrollment={e} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Schedule block */}
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">Lịch học hằng tuần</h2>
            {schedules.length === 0 ? (
              <EmptySection message="Lớp chưa có lịch học. Liên hệ quản lý cơ sở." />
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {schedules.map((s) => (
                  <ScheduleCard key={s.id} schedule={s} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Student card (mobile)
// ---------------------------------------------------------------------------
function StudentCard({ enrollment: e }: { enrollment: RosterEnrollment }) {
  const age = calcAge(e.student.dateOfBirth);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-full text-left rounded-lg border border-border bg-card p-3 flex items-start gap-3 hover:bg-accent/50 transition-colors min-h-[44px]">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-foreground truncate">{e.student.fullName}</span>
              <span className="font-mono text-xs text-muted-foreground">{e.student.studentCode}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {age} tuổi · {e.student.gender === 'male' ? 'Nam' : e.student.gender === 'female' ? 'Nữ' : '—'}
            </p>
          </div>
          <Badge variant="outline" className="text-xs shrink-0">
            {e.status === 'active' ? 'Đang học' : e.status}
          </Badge>
        </button>
      </PopoverTrigger>
      <StudentPopover enrollment={e} />
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Student table row (desktop)
// ---------------------------------------------------------------------------
function StudentRow({ enrollment: e }: { enrollment: RosterEnrollment }) {
  const age = calcAge(e.student.dateOfBirth);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <tr className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors">
          <td className="px-4 py-3 font-mono text-xs">{e.student.studentCode}</td>
          <td className="px-4 py-3 font-medium">{e.student.fullName}</td>
          <td className="px-4 py-3 text-muted-foreground">{age}</td>
          <td className="px-4 py-3 text-muted-foreground">
            {e.student.gender === 'male' ? 'Nam' : e.student.gender === 'female' ? 'Nữ' : '—'}
          </td>
          <td className="px-4 py-3 text-muted-foreground">
            {e.enrolledAt ? new Date(e.enrolledAt).toLocaleDateString('vi-VN') : '—'}
          </td>
          <td className="px-4 py-3">
            <Badge variant="outline" className="text-xs">
              {e.status === 'active' ? 'Đang học' : e.status}
            </Badge>
          </td>
          <td className="px-4 py-3 text-xs text-primary underline-offset-2">
            Xem
          </td>
        </tr>
      </PopoverTrigger>
      <StudentPopover enrollment={e} />
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Student contact popover
// ---------------------------------------------------------------------------
function StudentPopover({ enrollment: e }: { enrollment: RosterEnrollment }) {
  const hasContact = e.student.phone || e.student.email;
  return (
    <PopoverContent className="w-64 p-4 space-y-2" align="start">
      <p className="font-semibold text-sm">{e.student.fullName}</p>
      <div className="text-xs space-y-1 text-muted-foreground">
        {e.student.phone ? (
          <p>📞 {e.student.phone}</p>
        ) : null}
        {e.student.email ? (
          <p>✉️ {e.student.email}</p>
        ) : null}
        {!hasContact && <p className="italic">Chưa có thông tin liên hệ</p>}
        {e.student.note && (
          <p className="mt-2 px-2 py-1.5 rounded bg-brand-orange-50 text-brand-orange-700 border border-brand-orange-200 dark:bg-brand-orange-950/30 dark:text-brand-orange-300 dark:border-brand-orange-700/50">
            ⚠️ {e.student.note}
          </p>
        )}
      </div>
    </PopoverContent>
  );
}

// ---------------------------------------------------------------------------
// Schedule card
// ---------------------------------------------------------------------------
function ScheduleCard({ schedule: s }: { schedule: TeacherClassSchedule }) {
  const dayLabel = s.weekday ? WEEKDAY_LABELS[s.weekday] ?? s.weekday : 'Một lần';
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-sm">
      <p className="font-medium text-foreground">{dayLabel}</p>
      <p className="text-muted-foreground text-xs mt-0.5">
        {s.startTime} – {s.endTime}
        {s.roomName && <span> · {s.roomName}</span>}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty section
// ---------------------------------------------------------------------------
function EmptySection({ message }: { message: string }) {
  return (
    <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>
  );
}
